import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { logger } from '@/utils/logger'
import {
  calculatePasswordStrength,
  checkPasswordsMatch,
  validatePassword
} from '@/utils/passwordHelpers'

export type MessageType = 'success' | 'error' | ''

export interface UseAuthenticationReturn {
  isLoading: boolean
  message: string
  messageType: MessageType
  passwordStrength: number
  passwordsMatch: boolean | null
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>
  loginWithOAuth: (provider: 'google' | 'facebook' | 'apple') => Promise<void>
  clearMessage: () => void
  updatePasswordStrength: (password: string) => void
  updatePasswordsMatch: (password: string, confirmPassword: string) => void
}

export function useAuthentication(
  onSuccess?: () => void
): UseAuthenticationReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<MessageType>('')
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [passwordsMatch, setPasswordsMatch] = useState<boolean | null>(null)

  const clearMessage = () => {
    setMessage('')
    setMessageType('')
  }

  const updatePasswordStrength = (password: string) => {
    setPasswordStrength(calculatePasswordStrength(password))
  }

  const updatePasswordsMatch = (password: string, confirmPassword: string) => {
    setPasswordsMatch(checkPasswordsMatch(password, confirmPassword))
  }

  const login = async (email: string, password: string) => {
    setIsLoading(true)
    clearMessage()

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        setMessageType('error')
        setMessage(
          error.message === 'Invalid login credentials'
            ? 'כתובת מייל או סיסמה שגויים'
            : 'שגיאה בהתחברות'
        )
      } else {
        setMessageType('success')
        setMessage('התחברת בהצלחה!')
        onSuccess?.()
      }
    } catch (error) {
      setMessageType('error')
      setMessage('שגיאה בהתחברות')
      logger.error('Login error', {
        component: 'useAuthentication',
        action: 'login',
        data: error
      })
    } finally {
      setIsLoading(false)
    }
  }

  const signup = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    const validation = validatePassword(password)
    if (!validation.isValid) {
      setMessageType('error')
      setMessage(validation.error!)
      return
    }

    setIsLoading(true)
    clearMessage()

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`
          }
        }
      })

      if (error) {
        setMessageType('error')
        if (error.message === 'User already registered') {
          setMessage('משתמש כבר רשום במערכת')
        } else if (error.message.includes('email') || error.message.includes('invalid')) {
          setMessage(
            "כתובת המייל אינה תקינה - השתמש במייל אמיתי (gmail.com, outlook.com וכו')"
          )
        } else if (error.message.includes('password')) {
          setMessage('הסיסמה אינה תקינה או חלשה מדי')
        } else if (error.message.includes('signup')) {
          setMessage('הרישום אינו מופעל במערכת - פנה למנהל')
        } else {
          setMessage(`שגיאה ברישום: ${error.message}`)
        }
      } else {
        setMessageType('success')
        setMessage('נרשמת בהצלחה! בדוק את המייל לאישור החשבון')
      }
    } catch (error) {
      setMessageType('error')
      setMessage('שגיאה ברישום')
    } finally {
      setIsLoading(false)
    }
  }

  const loginWithOAuth = async (provider: 'google' | 'facebook' | 'apple') => {
    setIsLoading(true)

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) {
        setMessageType('error')
        const providerNames = {
          google: 'Google',
          facebook: 'Facebook',
          apple: 'Apple'
        }
        setMessage(`שגיאה בהתחברות עם ${providerNames[provider]}`)
      }
    } catch (error) {
      setMessageType('error')
      setMessage('שגיאה בהתחברות')
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    message,
    messageType,
    passwordStrength,
    passwordsMatch,
    login,
    signup,
    loginWithOAuth,
    clearMessage,
    updatePasswordStrength,
    updatePasswordsMatch
  }
}
